import { SyntheticEvent, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { FormLabel, SelectionCardGroup, SelectionCardGroupProps, Typography } from '@optimus-web/core';

export enum PlatformProfilesRole {
    Admin = 'Admin',
    Support = 'Support',
    Developer = 'Developer',
}

interface RoleFieldProps
    extends Omit<SelectionCardGroupProps, 'cardVariant' | 'options' | 'compact' | 'onChange' | 'type' | 'inputValue'> {
    name: string;
}

export const RoleField = ({ name, ...props }: RoleFieldProps) => {
    const roleOptions = useRoleFieldOptions(name);
    const { setValue, watch } = useFormContext<Record<string, PlatformProfilesRole>>();
    const value = watch(name);
    const handleChange = (_event: SyntheticEvent<HTMLElement>, newValue: string) => {
        setValue(name, newValue as PlatformProfilesRole, { shouldDirty: true });
    };

    return (
        <div>
            <FormLabel required>
                <LocalizedText textKey={TextKey.Role} />
            </FormLabel>
            <SelectionCardGroup
                type="radio"
                cardVariant="horizontal"
                options={roleOptions}
                compact
                inputValue={value}
                onChange={handleChange}
                {...props}
            />
        </div>
    );
};

const roleOptionsData = [
    {
        label: TextKey.PlatformProfilePermissionTypeAdminTitle,
        value: PlatformProfilesRole.Admin,
        description: TextKey.PlatformProfilePermissionTypeAdminDescription,
    },
    {
        label: TextKey.PlatformProfilePermissionTypeSupportTitle,
        value: PlatformProfilesRole.Support,
        description: TextKey.PlatformProfilePermissionTypeSupportDescription,
    },
    {
        label: TextKey.PlatformProfilePermissionTypeDeveloperTitle,
        value: PlatformProfilesRole.Developer,
        description: TextKey.PlatformProfilePermissionTypeDeveloperDescription,
    },
];

const useRoleFieldOptions = (name: string) =>
    useMemo(
        () =>
            roleOptionsData.map(({ label, value, description }) => ({
                label: (
                    <Typography textStyle="bodySmallStrong" as="div">
                        <LocalizedText textKey={label} />
                    </Typography>
                ),
                value,
                name,
                description: (
                    <Typography textStyle="bodySmallStrong" variant="secondary" as="div">
                        <LocalizedText textKey={description} />
                    </Typography>
                ),
                dataTestId: value,
            })),
        [name]
    );
